// Lightweight admin tools: population, AI inspection, simulation, logs, balance settings.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, ago, fmt } from '../state.tsx';
import { Loading, useLoad, PlayerLink, Panel } from '../components/ui.tsx';

export function AdminPage({ tab = 'world' }: { tab?: string }) {
  const tabs: [string, string][] = [['world', 'World'], ['events', 'Events'], ['players', 'Players'], ['fights', 'Fights'], ['config', 'Balance'], ['logs', 'Logs']];
  return (
    <div>
      <h1 className="page-title">Admin <span className="admin-tag">administrators only</span></h1>
      <div className="subnav" style={{ marginBottom: 8 }}>
        {tabs.map(([id, label]) => <a key={id} className={tab === id ? 'active' : ''} href={`#/admin/${id}`}>{label}</a>)}
      </div>
      {tab === 'events' ? <Events /> : tab === 'players' ? <Players /> : tab === 'fights' ? <Fights /> : tab === 'config' ? <Config /> : tab === 'logs' ? <Logs /> : <World />}
    </div>
  );
}

function World() {
  const { setFlash } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/admin/overview'), []);
  const [target, setTarget] = useState('');
  if (!data) return <Loading />;
  const run = async (fn: () => Promise<any>, msg: (r: any) => string) => {
    try {
      const r = await fn();
      setFlash({ kind: 'good', text: msg(r) });
      reload();
    } catch (e) {
      setFlash({ kind: 'bad', text: (e as Error).message });
    }
  };
  return (
    <div>
      <div className="row">
        <Panel title="Population">
          <div className="stat-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div><span>Human players</span><b>{data.humans}</b></div>
            <div><span>Active AI players</span><b>{data.aiActive}</b></div>
            <div><span>Retired AI players</span><b>{data.aiRetired}</b></div>
            <div><span>AI sessions due now</span><b>{data.aiDueNow}</b></div>
            <div><span>AI sessions in the last hour</span><b>{data.sessionsLastHour}</b></div>
            <div><span>Fights in the last day</span><b>{data.fightsLastDay}</b></div>
            <div><span>Worker errors (24h)</span><b className={data.workerErrorsLastDay ? 'bad' : ''}>{data.workerErrorsLastDay}</b></div>
          </div>
        </Panel>
        <Panel title="Controls">
          <p><button className="btn" onClick={() => run(() => api.post('/api/admin/ai/run', { count: 10 }), (r) => `Ran ${r.ran} due AI session(s).`)}>Run due AI sessions now</button></p>
          <form onSubmit={(e) => { e.preventDefault(); run(() => api.post('/api/admin/ai/population', { target: Number(target) }), (r) => `Added ${r.added}, reactivated ${r.reactivated}, retired ${r.retired}.`); }}>
            Target AI population: <input value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: 60 }} placeholder={String(data.aiActive)} /> <button className="btn small">Set</button>
          </form>
          <p><button className="btn gray small" onClick={() => run(() => api.post('/api/admin/test-account', {}), (r) => `Test account created. Username: ${r.username}  Password: ${r.password}`)}>Create test account</button></p>
        </Panel>
      </div>
      <div className="row">
        <Panel title="AI personalities">
          <table className="list"><tbody>
            {data.personalityDefs.map((p: any) => (
              <tr key={p.id}><td><b>{p.label}</b><div className="small muted">{p.description}</div></td><td className="right">{data.personalities.find((x: any) => x.personality === p.id)?.n ?? 0}</td></tr>
            ))}
          </tbody></table>
        </Panel>
        <Panel title="Level spread">
          <table className="list"><thead><tr><th>Levels</th><th>AI</th><th>Human</th></tr></thead><tbody>
            {data.levelSpread.map((b: any) => <tr key={b.bucket}><td>{b.bucket}–{b.bucket + 9}</td><td>{b.ai}</td><td>{b.human}</td></tr>)}
          </tbody></table>
        </Panel>
      </div>
    </div>
  );
}

function Players() {
  const { setFlash } = useGame();
  const [type, setType] = useState('all');
  const [inspect, setInspect] = useState<number | null>(null);
  const { data, reload } = useLoad(() => api.get(`/api/admin/players?type=${type}`), [type]);
  const detail = useLoad(() => (inspect ? api.get(`/api/admin/ai/${inspect}`) : Promise.resolve(null)), [inspect]);
  const resetLink = async (userId: number) => {
    try {
      const r = await api.post('/api/admin/reset-link', { userId });
      setFlash({ kind: 'good', text: <>Send this one-time link to the player (valid 24 hours): <code>{`${window.location.origin}/${r.path}`}</code></> });
    } catch (e) {
      setFlash({ kind: 'bad', text: (e as Error).message });
    }
  };
  return (
    <div>
      <div className="subnav" style={{ background: '#000', marginBottom: 6 }}>
        {['all', 'human', 'ai'].map((t) => <a key={t} className={type === t ? 'active' : ''} onClick={() => setType(t)}>{t}</a>)}
      </div>
      {inspect && detail.data && (
        <Panel title={<>AI inspector: {detail.data.state.name} <span className="ai-tag">{detail.data.agent.personality}</span></>} right={<a onClick={() => setInspect(null)}>close</a>}>
          <div className="row">
            <div className="grow">
              <div>Level {detail.data.state.level} · {money(detail.data.state.cash)} cash · {money(detail.data.state.bank)} bank · mafia {detail.data.state.mafiaSize}</div>
              <div>Energy {detail.data.state.energy}/{detail.data.state.maxEnergy} · Stamina {detail.data.state.stamina}/{detail.data.state.maxStamina} · Health {detail.data.state.health}/{detail.data.state.maxHealth}</div>
              <div>Sessions played: {detail.data.agent.sessions} · next session {ago(detail.data.agent.next_action_at) === 'just now' ? 'now' : new Date(detail.data.agent.next_action_at).toLocaleString()}</div>
              <h2>Last session</h2>
              {detail.data.agent.last_summary ? (
                <ul className="small">{detail.data.agent.last_summary.actions.map((a: string, i: number) => <li key={i}>{a}</li>)}{detail.data.agent.last_summary.errors.map((a: string, i: number) => <li key={`e${i}`} className="bad">{a}</li>)}</ul>
              ) : <div className="muted">None yet.</div>}
              <button className="btn small" onClick={async () => { await api.post('/api/admin/ai/run', { playerId: inspect }); detail.reload(); reload(); }}>Run a session now</button>
            </div>
            <div style={{ width: 280 }}>
              <h2>Traits</h2>
              <pre className="json">{JSON.stringify(detail.data.agent.traits, null, 1)}</pre>
            </div>
          </div>
        </Panel>
      )}
      {!data ? <Loading /> : (
        <table className="list stack">
          <thead><tr><th>Name</th><th>Type</th><th>Level</th><th>Cash / bank</th><th>Jobs</th><th>W/L</th><th>Last seen / next</th><th></th></tr></thead>
          <tbody>
            {data.players.map((p: any) => (
              <tr key={p.id}>
                <td><PlayerLink id={p.id} name={p.name} /></td>
                <td>{p.isAi ? <span className="ai-tag">AI · {p.personality}{p.retired ? ' (retired)' : ''}</span> : <>{p.username} {p.role === 'admin' && <span className="admin-tag">admin</span>}{p.disabled ? <span className="bad"> disabled</span> : ''}</>}</td>
                <td>{p.level}</td>
                <td className="small">{money(p.cash)} / {money(p.bank)}</td>
                <td>{fmt(p.jobsDone)}</td>
                <td>{p.fightsWon}/{p.fightsLost}</td>
                <td className="small">{p.isAi ? (p.nextActionAt ? new Date(p.nextActionAt).toLocaleTimeString() : '') : ago(p.lastSeenAt)}</td>
                <td className="right small">
                  {p.isAi ? <a onClick={() => setInspect(p.id)}>inspect</a> : (
                    <>
                      <a onClick={() => resetLink(p.userId)}>reset link</a>{p.role !== 'admin' && <> · <a onClick={async () => { await api.post('/api/admin/user-disabled', { userId: p.userId, disabled: !p.disabled }); reload(); }}>{p.disabled ? 'enable' : 'disable'}</a></>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Fights() {
  const { data } = useLoad(() => api.get('/api/admin/fights'), []);
  const [id, setId] = useState<number | null>(null);
  const detail = useLoad(() => (id ? api.get(`/api/admin/fights/${id}`) : Promise.resolve(null)), [id]);
  if (!data) return <Loading />;
  return (
    <div>
      {detail.data && <Panel title={`Fight #${id} calculation`} right={<a onClick={() => setId(null)}>close</a>}><pre className="json">{JSON.stringify(detail.data, null, 2)}</pre></Panel>}
      <table className="list stack">
        <thead><tr><th>#</th><th>Attacker</th><th>Defender</th><th>Attack vs defense</th><th>P(win)</th><th>Result</th><th>Cash</th><th></th></tr></thead>
        <tbody>
          {data.fights.map((f: any) => (
            <tr key={f.id}>
              <td>{f.id}</td><td>{f.attacker}</td><td>{f.defender}</td>
              <td>{fmt(f.attackPower)} vs {fmt(f.defensePower)}</td><td>{Math.round(f.winChance * 100)}%</td>
              <td>{f.attackerWon ? <span className="good">attacker</span> : <span className="bad">defender</span>}</td><td>{money(f.cash)}</td>
              <td><a onClick={() => setId(f.id)}>details</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Config() {
  const { setFlash } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/admin/config'), []);
  const [edit, setEdit] = useState<Record<string, string>>({});
  if (!data) return <Loading />;
  const save = async (key: string, value: number | null) => {
    try {
      await api.post('/api/admin/config', { key, value });
      setFlash({ kind: 'good', text: `${key} ${value === null ? 'reset to default' : `set to ${value}`}.` });
      reload();
    } catch (e) {
      setFlash({ kind: 'bad', text: (e as Error).message });
    }
  };
  let group = '';
  return (
    <div>
      <p className="muted">These numbers drive the game. Changes take effect immediately for everyone. See DESIGN_NOTES.md for what each one does.</p>
      <table className="list stack">
        <tbody>
          {data.settings.map((s: any) => {
            const g = s.key.split('.')[0];
            const header = g !== group ? ((group = g), <tr key={`h${g}`}><td colSpan={4} className="gold"><b>{g}</b></td></tr>) : null;
            return [header, (
              <tr key={s.key}>
                <td>{s.key.split('.')[1]}</td>
                <td>{s.overridden ? <b className="gold">{s.value}</b> : s.value} <span className="dim small">(default {s.default})</span></td>
                <td><input style={{ width: 90 }} value={edit[s.key] ?? ''} onChange={(e) => setEdit({ ...edit, [s.key]: e.target.value })} placeholder="new value" /></td>
                <td className="right">
                  <button className="btn small" onClick={() => save(s.key, Number(edit[s.key]))}>Set</button>
                  {s.overridden && <> <button className="btn small gray" onClick={() => save(s.key, null)}>Default</button></>}
                </td>
              </tr>
            )];
          })}
        </tbody>
      </table>
    </div>
  );
}

function Logs() {
  const { data } = useLoad(() => api.get('/api/admin/logs'), []);
  if (!data) return <Loading />;
  return (
    <div className="row">
      <Panel title="Background worker">
        {data.worker.length === 0 ? <div className="muted">No worker messages. That’s good.</div> : data.worker.map((w: any) => (
          <div key={w.id} className="feed-item"><span className={w.level === 'error' ? 'bad' : ''}>{w.message}</span><span className="when">{ago(w.created_at)}</span></div>
        ))}
      </Panel>
      <Panel title="Audit log">
        {data.audit.map((a: any) => (
          <div key={a.id} className="feed-item"><span><b>{a.action}</b> {a.username ?? ''} <span className="dim small">{a.ip ?? ''}</span></span><span className="when">{ago(a.created_at)}</span></div>
        ))}
      </Panel>
    </div>
  );
}

/** Start world events for everyone: Double XP, Police Crackdown, Bounty Frenzy. */
function Events() {
  const { setFlash, refresh } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/admin/events'), []);
  const [hours, setHours] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Record<string, string>>({});
  if (!data) return <Loading />;
  const start = async (kind: string) => {
    try {
      const r = await api.post('/api/admin/events/start', { kind, hours: Number(hours[kind] ?? 2), message: message[kind] ?? '' });
      setFlash({ kind: 'good', text: r.extended ? `${data.kinds[kind].name} extended.` : `${data.kinds[kind].name} has started! Everyone sees a banner and it’s in the Mafia News.` });
      reload();
      refresh();
    } catch (e) {
      setFlash({ kind: 'bad', text: (e as Error).message });
    }
  };
  const end = async (id: number) => {
    await api.post('/api/admin/events/end', { id });
    reload();
    refresh();
  };
  return (
    <div>
      <p className="muted">Start an event for the whole city. It applies to real and computer players alike, shows a banner on every page, and is announced in the Mafia News. Starting an event that’s already running extends it.</p>
      {Object.entries(data.kinds).map(([kind, k]: [string, any]) => {
        const running = data.active.find((e: any) => e.kind === kind);
        return (
          <Panel key={kind} title={<>{k.name} {running && <span className="good small">· running, ends in {Math.ceil((running.endsAt - Date.now()) / 60_000)} min</span>}</>}>
            <div className="small" style={{ marginBottom: 6 }}>{k.description}</div>
            <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
              <span>For</span>
              <select value={hours[kind] ?? '2'} onChange={(e) => setHours({ ...hours, [kind]: e.target.value })}>
                {[1, 2, 4, 8, 12, 24, 48, 72].map((h) => <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>)}
              </select>
              <input className="grow" placeholder="Optional message to everyone (e.g. Happy Friday!)" maxLength={200} value={message[kind] ?? ''} onChange={(e) => setMessage({ ...message, [kind]: e.target.value })} />
              <button className="btn green" onClick={() => start(kind)}>{running ? 'Extend' : 'Start'}</button>
              {running && <button className="btn gray" onClick={() => end(running.id)}>End now</button>}
            </div>
          </Panel>
        );
      })}
      <Panel title="Recent events">
        {data.history.length === 0 ? <div className="muted">None yet.</div> : data.history.map((e: any) => (
          <div key={e.id} className="feed-item">
            <span className="grow"><b>{data.kinds[e.kind]?.name ?? e.kind}</b>{e.message && <> · “{e.message}”</>}</span>
            <span className="when">{new Date(e.startsAt).toLocaleString()} → {new Date(e.endsAt).toLocaleString()}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
